import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout'; 
import { states } from '../states'; 
import { countries } from '../countries'; 
import { useNavigate } from 'react-router-dom';
import { useAuth } from './StoreValue';
import { markStepComplete } from './StepGuard';
import { isPromotix } from '../utils/agentConfig';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const ClientInfoForm = () => {
  
    const [clientInfo, setClientInfo] = useState({
        clientType: 'Merchant',
        dba: '',
        url: '',
        address: '',
        city: '',
        region: '',
        postal: '',
        country: 'USA',
        phone: '',
        theme: 'Default',
        timeZone: '',
        email: '',
    });
    const [contactInfo, setContactInfo] = useState({
        firstName: '',  
        lastName: '',
        guid:  ''
    });
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    // Reveal toggle for a masked email loaded from a saved application.
    const [showEmail, setShowEmail] = useState(false);
    const navigate = useNavigate();
  
  const { ContactGuid, agentIdVal, leadGuid, loginInfo, isvId, updateAgentId, updateContactGuid, updateLeadGuid } = useAuth();

  // The real email only exists client-side during this session (what was typed at login). TRX
  // returns the stored email masked and has no unmask API, so the eye can reveal the full address
  // only while we still have it — after a hard refresh, only the mask remains.
  const revealableEmail = (loginInfo?.email && !String(loginInfo.email).includes('*')) ? loginInfo.email : '';
  const emailIsMasked = !!clientInfo.email && String(clientInfo.email).includes('*');
  
    const handleChange = (e) => {
        const { name, value } = e.target;
        // Promotix: ZIP/postal is restricted to 5 digits — strip non-digits and cap length as typed.
        const v = (name === 'postal' && isPromotix(agentIdVal))
            ? value.replace(/\D/g, '').slice(0, 5)
            : value;

        if (name in clientInfo) {
            setClientInfo({
                ...clientInfo,
                [name]: v
            });
        } else if (name in contactInfo) {
            setContactInfo({
                ...contactInfo,
                [name]: v
            });
        }
    };

    useEffect(() => {
        if (loginInfo && (loginInfo.dba || loginInfo.phone || loginInfo.email)) {
            setClientInfo((prevClientInfo) => ({
                ...prevClientInfo,
                dba: loginInfo.dba || '',
                phone: loginInfo.phone || '',
                email: loginInfo.email || '',
            }));
        }
        const fetchLeadData = async () => {
            try {
                const response = await axios.get(`${API_BASE_URL}/api/Auth/LeadLookup`, {
                params: {
                    leadGuidVal: leadGuid,
                    agentIdValue: agentIdVal,
                    // Pass leadGuid here
                },
                });
                if(response.data.result===null)
                {
                    setClientInfo({
                        clientType: 'Merchant',
                        dba: loginInfo?.dba || '',
                        url: '',
                        address:  '',
                        city: '',
                        region: '',
                        postal:  '',
                        country:  'USA',
                        phone:  loginInfo?.phone || '',
                        theme:  'Default',
                        timeZone: '',
                        email:  loginInfo?.email || '',
                    });
                    
                    setContactInfo({
                        firstName: '',
                        lastName: '',
                        guid :''
                    });
                }
                else{
    
                if (response.data.result.responseCode === '00') {
                    const clientData = response.data.queryResponse.clientInfo;
                    const contacts = response.data.queryResponse.contacts;

                    // Merge, don't replace. Priority: a real (unmasked) stored value, then the
                    // login value (an ISV lead is cloned with Dba+Phone only, so its email comes
                    // back blank and must be kept from loginInfo or it is lost at submit), and as a
                    // last resort the masked stored value — so a returning merchant still sees that
                    // their email is saved rather than an empty box. Re-saving a masked value is
                    // safe: the submit guards below skip anything containing "***".
                    const keep = (serverVal, loginVal) =>
                        (serverVal && !String(serverVal).includes('*')) ? serverVal : (loginVal || serverVal || '');

                    setClientInfo({
                        clientType: 'Merchant',
                        dba: keep(clientData.dba, loginInfo?.dba),
                        url: clientData.url || '',
                        address: clientData.address || '',
                        city: clientData.city || '',
                        region: clientData.region || '',
                        postal: clientData.postal || '',
                        country: clientData.country || 'USA',
                        phone: keep(clientData.phone, loginInfo?.phone),
                        theme: clientData.theme || 'Default',
                        timeZone: clientData.timeZone || '',
                        // Email keeps the stored value AS-IS (masked, e.g. "sha***sai@x.com") when
                        // one exists, so a returning merchant sees it is saved but protected; the eye
                        // toggle on the field reveals the real address from this session's login. Only
                        // fall back to loginInfo when nothing is stored. Submit guards skip "***".
                        email: clientData.email || loginInfo?.email || '',
                    });

                    if (contacts !== null) {
                        const firstContact = contacts.contact[0];
                        updateContactGuid(firstContact.guid);
                        setContactInfo({
                            firstName: firstContact.firstName || '',
                            lastName: firstContact.lastName || '',
                            guid: ContactGuid || ''
                        });
                    }
                }
                }
            } catch (error) {
                console.error('Error fetching lead data:', error);
            }
        };
    
        fetchLeadData();
    }, [loginInfo]);

const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage('');
    setSuccessMessage('');
    setPhoneError(''); 
    setLoading(true);
    const missingFields = [];
    if (!clientInfo.dba) missingFields.push('DBA');
    if (!clientInfo.url) missingFields.push('URL');
    if (!clientInfo.address) missingFields.push('Address');
    if (!clientInfo.city) missingFields.push('City');
    if (!clientInfo.region) missingFields.push('State');
    if (!clientInfo.postal) missingFields.push('Postal');
    if (!clientInfo.phone) missingFields.push('Phone');
    if (!clientInfo.timeZone) missingFields.push('Time Zone');
    if (!clientInfo.email) missingFields.push('Email');
    if (!contactInfo.firstName) missingFields.push('Contact First Name');
    if (!contactInfo.lastName) missingFields.push('Contact Last Name');
    if (missingFields.length > 0) {
        setErrorMessage(`Please fill in the following required fields: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
    }

    if (phoneError) {
        setErrorMessage(phoneError);
        setLoading(false);
        return; // Stop form submission
    }

    // Promotix: ZIP/postal must be exactly 5 digits.
    if (isPromotix(agentIdVal) && !/^\d{5}$/.test(clientInfo.postal)) {
        setErrorMessage('ZIP code must be exactly 5 digits');
        setLoading(false);
        return;
    }

    // Check if the input starts with "http://" or "https://"
    if (!clientInfo.url.startsWith('http://') && !clientInfo.url.startsWith('https://')) {
        // If not, prepend "https://"
        clientInfo.url = 'https://' + clientInfo.url;
    }
    
    // Deferred ISV clone. The ISV lead is no longer created at login — if this is a new ISV
    // application (marked "ISV_Flag" with no leadGuid yet), clone the template now, on this first
    // save, so landing on this page and leaving without saving never creates an orphan lead. After
    // the clone the lead exists exactly once and the normal update + Owner-contact append below run
    // against it. Non-ISV (no isvId / not ISV_Flag) and resumed leads (leadGuid already set) skip this.
    let effectiveLeadGuid = leadGuid;
    if (!effectiveLeadGuid && ContactGuid === "ISV_Flag" && isvId) {
        try {
            const cloneResp = await axios.post(`${API_BASE_URL}/api/Auth/ClientLookup`, {
                agentId: agentIdVal,
                isvId,
                Dba: clientInfo.dba,
                Phone: formatPhoneNumber(clientInfo.phone),
                Detail: { TranType: "TranType", TranAction: "TranAction", Ip: 'Ip' },
                Reference: { Client: isvId },
            });
            if (typeof cloneResp.data === 'string' && cloneResp.data.includes("OK")) {
                const parts = cloneResp.data.split('-');
                if (parts.length >= 3) {
                    effectiveLeadGuid = parts[2];
                    updateLeadGuid(effectiveLeadGuid);
                }
            }
        } catch (cloneErr) {
            console.error('ISV deferred clone failed:', cloneErr);
        }
        if (!effectiveLeadGuid) {
            setErrorMessage('Unable to create the merchant lead. Please try again or contact support.');
            setLoading(false);
            return;
        }
    }

    const commonData = {
    leadguidVal: effectiveLeadGuid,
    agentId: agentIdVal,
    Detail: {
        TranType: "TranType",
        TranAction: "TranAction",
        Ip: "Ip",
    },
    Lead: {
        LeadState: "New",
    },
    ClientInfo: {
        clientType: "Merchant",
        dba: clientInfo.dba,
        url: clientInfo.url,
        address: clientInfo.address,
        city: clientInfo.city,
        region: clientInfo.region,
        postal: clientInfo.postal,
        country: clientInfo.country,
        phone: formatPhoneNumber(clientInfo.phone),
        theme: clientInfo.theme,
        timeZone: clientInfo.timeZone,
    },
};

const data = {
    ...commonData,
    ...(ContactGuid !== null && { contactGuidVal: ContactGuid }), // Conditionally add contactGuidVal
};
if (ContactGuid) { // Assuming contactInfo.guid determines if Contact block is added
    data.Contact = {
        guid: ContactGuid, // Use contactInfo.guid here
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phone: formatPhoneNumber(clientInfo.phone), // keep Contact.Phone present so a contact update re-asserts it (resume match)
        postal: clientInfo.postal,
        role: "Owner",
        contactOrder: "1",
        ownershipPercent: "100",
        country :"USA"
    };
} else  { // If there's no contactInfo.guid but ContactGuid exists, add basic contact
    data.Contact = {
        firstName: contactInfo.firstName,
        lastName: contactInfo.lastName,
        phone: formatPhoneNumber(clientInfo.phone), // seed Contact.Phone so non-ISV resume matches on return
        postal: clientInfo.postal,
        role: "Owner",
        contactOrder: "1",
        ownershipPercent: "100",
        country :"USA"
    };
}
           
          
        // Add the email only if it's valid (not masked)
          if (clientInfo.email && !clientInfo.email.includes("*")) {
            data.ClientInfo.email = clientInfo.email;
          }
          // Seed the Owner contact's email from the login/ClientInfo email so that resume works
          // from the very first save. Resume matches on the contact email (TRX cannot filter on
          // ClientInfo.Email — its query schema rejects it), so the Owner contact must carry the
          // login email in both flows, not only after the Contacts step. The Contacts step can
          // still refine it later. (For ISV the backend strips this Contact on the update and the
          // separate Owner append below carries the email instead.)
          const ownerContactEmail =
            (contactInfo.email && !contactInfo.email.includes("*")) ? contactInfo.email :
            (clientInfo.email && !clientInfo.email.includes("*")) ? clientInfo.email : '';
          if (ownerContactEmail) {
            data.Contact.email = ownerContactEmail;
          }

        try {
            const response = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, data);
            var responseCode = response.data.response.result.responseCode;
            var errorInfo = response.data.response?.result?.debugInfo;
            
             if (responseCode === '00') 
            {
             const responseData = response.data;  
             if(responseData.response?.lead?.guid)
                {
             let receivedLeadGuid = responseData.response.lead.guid;
             let receivedContactGuid = responseData.response.contact.guid;
             updateLeadGuid(receivedLeadGuid);
             updateContactGuid(receivedContactGuid); 
                }

              if (!effectiveLeadGuid && !responseData.response?.lead?.guid) {
                setErrorMessage('Unable to create or retrieve the merchant lead. Please try again or contact support.');
                return;
              }

              // ISV leads already exist when ClientInfo is saved, so the update above cannot also
              // create the Owner contact — TRX rejects a contact-create that carries ClientInfo.
              // Create it now in a separate Lead/Append so the name (and email) land on the contact
              // immediately, matching the non-ISV flow where lead+contact are created together.
              // "ISV_Flag" marks an ISV lead that has no real contact yet; after this append we hold
              // the real guid so the Contacts step updates this same Owner instead of adding a dup.
              effectiveLeadGuid = effectiveLeadGuid || responseData.response?.lead?.guid;
              if (ContactGuid === "ISV_Flag" && effectiveLeadGuid && (contactInfo.firstName || contactInfo.lastName)) {
                const ownerAppend = {
                  leadguidVal: effectiveLeadGuid,
                  agentId: agentIdVal,
                  Detail: { TranType: "TranType", TranAction: "TranAction", Ip: "Ip" },
                  Contact: {
                    firstName: contactInfo.firstName,
                    lastName: contactInfo.lastName,
                    // Seed the contact phone from the login phone. The resume LeadQuery in Login.js
                    // AND-matches Contact.Phone + Contact.Email, so without this the just-created ISV
                    // lead can never be found on return and ClientLookup clones a duplicate instead.
                    // Same formatPhoneNumber() + clientInfo.phone (seeded from loginInfo.phone) the
                    // query uses, so the stored value matches the filter byte-for-byte.
                    phone: formatPhoneNumber(clientInfo.phone),
                    postal: clientInfo.postal,
                    role: "Owner",
                    contactOrder: "1",
                    ownershipPercent: "100",
                    country: "USA",
                  },
                };
                if (clientInfo.email && !clientInfo.email.includes("*")) {
                  ownerAppend.Contact.email = clientInfo.email;
                }
                try {
                  const ownerResp = await axios.post(`${API_BASE_URL}/api/Auth/LeadModify`, ownerAppend);
                  const newGuid = ownerResp.data?.response?.contact?.guid;
                  if (newGuid) updateContactGuid(newGuid);
                } catch (ownerErr) {
                  console.error('ISV owner-contact append failed:', ownerErr);
                }
              }

              markStepComplete('clientInfo');
              // Promotix: Pricing comes next (between Client and Corporate); all others go to Corporate.
              navigate(isPromotix(agentIdVal) ? '/PricingInfo' : '/CorporateInfo');
            }
            else {
                setErrorMessage(errorInfo);
            }
        } catch (error) {
            console.error('Error submitting form:', error);
            const serverMsg = error.response?.data?.message || error.response?.data || error.message;
            setErrorMessage(typeof serverMsg === 'string' ? serverMsg : 'An error occurred during submission.');
        }finally {
            setLoading(false);  // SStop loading after response
        }
    };

    const [phoneError, setPhoneError] = useState('');

    // Phone number formatting function
    const formatPhoneNumber = (phone) => {
        // Remove all non-digit characters
        const digits = phone.replace(/\D/g, '');
        
        // Format phone number with dashes if it has 10 digits
        if (digits.length === 10) {
            return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3');
        }
        return phone; // Return as is if not 10 digits
    };
    const handlePhoneBlur = () => {
        const formattedPhone = formatPhoneNumber(clientInfo.phone);
        setClientInfo({ ...clientInfo, phone: formattedPhone });

        // Validate if phone number has 10 digits
        const digits = clientInfo.phone.replace(/\D/g, '');
        if (digits.length !== 10) {
            setPhoneError('Phone number must contain exactly 10 digits with format with 711-111-1111');
        } else {
            setPhoneError('');
        }
    };
    return (
        <div>
             {loading && (
                <div className="loading-overlay">
                <div className="spinner-border" role="status">
                </div>
                </div>
        )}
            <Layout pageTitle="Client Information">
                <form onSubmit={handleSubmit}>
                    <div className="row">
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="clientType">Client Type <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="clientType"
                                name="clientType"
                                value="Merchant"
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="dba">DBA <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="dba"
                                name="dba"
                                value={clientInfo.dba}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="url">URL <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="url"
                                name="url"
                                value={clientInfo.url}
                                onChange={handleChange}
                                required
                            />
                        </div>
                    </div>
                    <div className="row">
                    <div className="col-md-4">
                            <label className='label_Control' htmlFor="firstName">Contact First Name <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="firstName"
                                name="firstName"
                                value={contactInfo.firstName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="lastName">Contact Last Name <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="lastName"
                                name="lastName"
                                value={contactInfo.lastName}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="country">Country <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <select
                                id="country"
                                name="country"
                                className="form-controling"
                                value={clientInfo.country}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a country</option>
                                {countries.map((country) => (
                                    <option key={country.abbreviation} value={country.abbreviation}>
                                        {country.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="address">Address <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="address"
                                name="address"
                                value={clientInfo.address}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="city">City <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="city"
                                name="city"
                                value={clientInfo.city}
                                onChange={handleChange}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="region">State <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <select
                                id="region"
                                name="region"
                                className="form-controling"
                                value={clientInfo.region}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select a state</option>
                                {states.map((state) => (
                                    <option key={state.abbreviation} value={state.abbreviation}>
                                        {state.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="postal">Postal <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="postal"
                                name="postal"
                                value={clientInfo.postal}
                                onChange={handleChange}
                                {...(isPromotix(agentIdVal) ? { maxLength: 5, inputMode: 'numeric' } : {})}
                                required
                            />
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="phone">Phone <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <input
                                type="text"
                                className="form-controling"
                                id="phone"
                                name="phone"
                                value={clientInfo.phone}
                                onChange={handleChange}
                                onBlur={handlePhoneBlur} // Add onBlur event
                                required
                            />
                            {phoneError && <p style={{ color: 'red' }}>{phoneError}</p>} {/* Display phone error */}
                        </div>
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="timeZone">Time Zone <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <select
                                className="form-controling"
                                id="timeZone"
                                name="timeZone"
                                value={clientInfo.timeZone}
                                onChange={handleChange}
                                required
                            >
                                <option value="">Select TimeZone</option>
                                <option value="EST">Eastern</option>
                                <option value="CST">Central</option>
                                <option value="MST">Mountain</option>
                                <option value="AST">Arizona</option>
                                <option value="PST">Pacific</option>
                                <option value="AKST">Alaskan Standard Time</option>
                                <option value="HST">Hawaiian Standard Time</option>
                            </select>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-md-4">
                            <label className='label_Control' htmlFor="email">Email <span style={{ color: 'red', display: 'contents' }}>*</span></label>
                            <div style={{ position: 'relative' }}>
                                <input
                                    type={emailIsMasked && showEmail ? 'text' : 'email'}
                                    className="form-controling"
                                    id="email"
                                    name="email"
                                    value={emailIsMasked && showEmail && revealableEmail ? revealableEmail : clientInfo.email}
                                    onChange={handleChange}
                                    onFocus={() => setShowEmail(false)}  // click to edit reverts to the editable field
                                    required
                                    style={emailIsMasked ? { paddingRight: '2.4rem' } : undefined}
                                />
                                {emailIsMasked && (
                                    <button
                                        type="button"
                                        onClick={() => setShowEmail((s) => !s)}
                                        disabled={!revealableEmail}
                                        title={revealableEmail
                                            ? (showEmail ? 'Hide email' : 'Show email')
                                            : 'Sign in again to view the full email'}
                                        aria-label={showEmail ? 'Hide email' : 'Show email'}
                                        style={{
                                            position: 'absolute', right: '0.6rem', top: '50%',
                                            transform: 'translateY(-50%)', background: 'none', border: 'none',
                                            padding: 0, lineHeight: 0,
                                            color: revealableEmail ? '#0d6efd' : '#adb5bd',
                                            cursor: revealableEmail ? 'pointer' : 'not-allowed',
                                        }}
                                    >
                                        {showEmail ? <FaEyeSlash /> : <FaEye />}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                    <div className="d-flex justify-content-center mt-3">
    <button type="submit" className="btn btn-success px-5" style={{ width: '370px' }}>
        Next
    </button>
</div>

                    {errorMessage && <p className="error-message" style={{ color: 'red' }}>{errorMessage}</p>}
                    {successMessage && <p className="success-message">{successMessage}</p>}
                    <p style={{ color: 'red' }}>* Required field</p>
                    
                </form>
            </Layout>
        </div>
    );
};

export default ClientInfoForm;
